import { computed } from 'vue'

import { usePageStore } from '@toni/rendering/src/stores/page'

import { useNativeTeasers } from '../../content-blocks/teaser/use-native-teasers.ts'
import { useContentBlock } from '../../../content-blocks/composables/use-content-block'

import { DEFAULT_TEASER_TYPE } from '.'

import type { TeaserOrNativeTeaser } from './teaser-group-constants'
import type { TeaserGroupSchema, TeaserSchema } from '../../../utils/content-block-schema/teaser-schema'

export type TeaserGroupProps = {
teasers: TeaserOrNativeTeaser[],
variant?:
  | 'teaser-group-single-big'
  | 'teaser-group-single-small'
  | 'teaser-group-double'
  | 'teaser-group-double-big'
  | 'teaser-group-triple'
  | 'teaser-group-triple-first'
  | 'teaser-group-triple-other'
  | 'teaser-group-quadruple',
  adaAutoNative?: boolean
}

const adaAutoNative = computed(() => usePageStore().pageData?.metaData?.adaAutoNative as boolean)

export const useTeaserGroups = (id) => {
  type TeaserGroupType =
    | 'teaser-group-single-big'
    | 'teaser-group-single-small'
    | 'teaser-group-double'
    | 'teaser-group-double-big'
    | 'teaser-group-triple'
    | 'teaser-group-triple-first'
    | 'teaser-group-triple-other'
    | 'teaser-group-quadruple'

  const teaserGroupTypeMap: Record<string, TeaserGroupType> = {
    'bx-teaser-container--full-width': 'teaser-group-single-big',
    'bx-teaser-container--full-width-two-cols': 'teaser-group-single-small',
    'bx-teaser-container--one-half-width-two-cols': 'teaser-group-double-big',
    'bx-teaser-container--one-half-width': 'teaser-group-double',
    'bx-teaser-container--one-third-width': 'teaser-group-triple',
    'bx-teaser-container--one-fourth-width': 'teaser-group-quadruple'
  }

  const teaserGroupType = computed((): TeaserGroupType => {
    const type = (blockData.value.paragraphType === 'pageableTeaserGroup' ? blockData.value.pageableTeaserGroup.teaserGroups.teaserGroupType : blockData.value.teaserGroup.teaserGroupType) || DEFAULT_TEASER_TYPE as string
    return teaserGroupTypeMap[type]
  })

  const teaserGroupVariant = computed((): TeaserGroupType | undefined => {
    if (adaAutoNative.value) {
      return 'teaser-group-quadruple'
    }
    if (teaserGroupType.value === 'teaser-group-triple') {
      return 'teaser-group-triple-first'
    }
    return teaserGroupType.value
  })

  // This component is used for both teaserGroup and pageableTeaserGroup
  const contentBlock = useContentBlock<'teaserGroup' | 'pageableTeaserGroup'>(id, 'teaserGroup')
  const blockData = computed(() => contentBlock.value.data)

  const composedTeasers = computed(() => {
    if (blockData.value.paragraphType === 'pageableTeaserGroup') {
      const teaserGroups = blockData.value.pageableTeaserGroup?.teaserGroups as TeaserGroupSchema[] | undefined
      return teaserGroups?.flatMap(group => group.teasers) || [] as TeaserSchema[]
    }
    return blockData.value.teaserGroup?.teasers || [] as TeaserSchema[]
  })

  const nativeTeaserOptions = computed(() => ({
    currentContentBlockId: id,
    teasers: composedTeasers.value || []
  }))

  const teasersWithNativeTeasers = useNativeTeasers(nativeTeaserOptions.value)

  const computedTeasers = computed<TeaserSchema[]>(() => {
    return adaAutoNative.value ? composedTeasers.value : teaserGroupType.value === 'teaser-group-triple' ? teasersWithNativeTeasers.value.slice(0, 1) : teasersWithNativeTeasers.value
  })

  // Only needed for teaser-group-triple
  const remainingTeasers = computed(() => {
    return teaserGroupType.value === 'teaser-group-triple' ? teasersWithNativeTeasers.value.slice(1) : []
  })

  return {
    computedTeasers,
    remainingTeasers,
    teaserGroupType,
    teaserGroupVariant
  }
}
